import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import * as yup from 'yup';

import { ExternalReference } from 'graphql-query-contracts';
import {
  ReferenceItemErrorMessage,
  EditableExternalReference,
  EditableExternalReferenceItem,
  mapExternalReferenceToEditable,
  filterValidExternalReferences,
} from '../types';

const createReferenceItemState = (
  reference?: ExternalReference
): EditableExternalReference => {
  if (reference) {
    // Use our mapper function and extract just the EditableExternalReference part
    const editableItem = mapExternalReferenceToEditable(reference);

    return {
      id: editableItem.id,
      direct: editableItem.direct,
      source: editableItem.source,
      relationshipSource: editableItem.relationshipSource,
    };
  }

  // Create empty state for new items
  return {
    id: v4(),
    direct: true,
    source: {
      sourceEntityId: undefined,
      sourceEntityType: undefined,
      sourceType: undefined,
    },
    relationshipSource: undefined,
  };
};

const deriveInitialState = (
  references: ExternalReference[]
): EditableExternalReference[] => {
  if (references.length > 0) {
    return references.map(createReferenceItemState);
  }

  return [createReferenceItemState()];
};

const emptyReferenceItemErrorMessage: ReferenceItemErrorMessage = {
  sourceEntityId: '',
  sourceType: '',
};

// Helper function to filter out incomplete external references
const filterCompleteExternalReferences = (
  references: EditableExternalReference[]
): ExternalReference[] => {
  // Convert to EditableExternalReferenceItem format and use our mapper
  const editableItems: EditableExternalReferenceItem[] = references.map(
    ref => ({
      ...ref,
      isRemovable: true,
      errorMessage: { sourceEntityId: '', sourceType: '' },
    })
  );

  return filterValidExternalReferences(editableItems);
};

export const useExternalReferenceItems = (
  references: ExternalReference[] = []
) => {
  const { t } = useTranslation();

  const [referenceItemsState, setReferenceItemsState] = useState<
    EditableExternalReference[]
  >(deriveInitialState(references));

  const [validationErrors, setValidationErrors] = useState<{
    [id: string]: ReferenceItemErrorMessage;
  }>({});

  const getIsRemovable = (item: EditableExternalReference) => {
    if (
      referenceItemsState.length === 1 &&
      !item.source.sourceEntityId &&
      !item.source.sourceType
    ) {
      return false;
    }

    return item.direct;
  };

  const getErrorMessage = (id: string): ReferenceItemErrorMessage =>
    validationErrors[id] || { ...emptyReferenceItemErrorMessage };

  const referenceItems: EditableExternalReferenceItem[] =
    referenceItemsState.map(item => ({
      ...item,
      isRemovable: getIsRemovable(item),
      errorMessage: getErrorMessage(item.id),
    }));

  const add = () => {
    const newReferenceItem = createReferenceItemState();

    setReferenceItemsState(prev => [...prev, newReferenceItem]);
  };

  const edit = (updatedItem: EditableExternalReferenceItem) => {
    setReferenceItemsState(prev =>
      prev.map(item =>
        item.id === updatedItem.id
          ? {
              ...item,
              source: updatedItem.source,
            }
          : item
      )
    );
  };

  const remove = (referenceItemId: string) => {
    setReferenceItemsState(prev => {
      if (prev.length === 1) {
        return [createReferenceItemState()];
      }

      return prev.filter(item => item.id !== referenceItemId);
    });
  };

  const handleValidationError = (
    validationError: yup.ValidationError,
    validatedItems: EditableExternalReferenceItem[]
  ) => {
    const errors: { [id: string]: ReferenceItemErrorMessage } = {};

    const sourceEntityIdFieldName: keyof ReferenceItemErrorMessage =
      'sourceEntityId';
    const sourceTypeFieldName: keyof ReferenceItemErrorMessage = 'sourceType';

    validationError.inner.forEach(error => {
      // Path format: "[0].sourceEntityId" or "[0].sourceType"
      const match = error.path?.match(
        /^\[(\d+)\].*(sourceEntityId|sourceType)$/
      );

      if (!match) {
        return;
      }

      const index = Number(match[1]);
      const errorField = match[2];
      const item = validatedItems[index];

      let fieldKey: keyof ReferenceItemErrorMessage;

      switch (errorField) {
        case sourceEntityIdFieldName:
          fieldKey = sourceEntityIdFieldName;
          break;
        case sourceTypeFieldName:
          fieldKey = sourceTypeFieldName;
          break;
        default:
          fieldKey = sourceEntityIdFieldName; // Default to sourceEntityId if not matched
          break;
      }

      if (!item) {
        return;
      }

      const id = item.id;

      if (!errors[id]) {
        errors[id] = { ...emptyReferenceItemErrorMessage };
      }

      errors[id][fieldKey] = error.message;
    });

    setValidationErrors(errors);
  };

  const validate = useCallback(async () => {
    // Convert to EditableExternalReferenceItem format for validation
    const itemsToValidate: EditableExternalReferenceItem[] =
      referenceItemsState.map(ref => ({
        ...ref,
        isRemovable: true,
        errorMessage: { sourceEntityId: '', sourceType: '' },
      }));

    // Filter out incomplete items - only validate items that have at least one field filled
    const itemsWithData = itemsToValidate.filter(item => {
      const hasSourceEntityId = !!item.source.sourceEntityId?.trim();
      const hasSourceType = !!item.source.sourceType;

      // Validate only when at least one field is provided. Skip when both are empty.
      return hasSourceEntityId || hasSourceType;
    });

    try {
      setValidationErrors({});
      await getValidationSchema(t).validate(itemsWithData, {
        abortEarly: false,
      });
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        handleValidationError(err, itemsWithData);
      }

      throw err;
    }
  }, [referenceItemsState, t]);

  const getCompleteReferences = (): ExternalReference[] => {
    return filterCompleteExternalReferences(referenceItemsState);
  };

  return {
    referenceItems,
    add,
    edit,
    remove,
    validate,
    getCompleteReferences,
  };
};

const getValidationSchema = (t: ReturnType<typeof useTranslation>['t']) => {
  const referenceItemSchema = yup.object().shape({
    source: yup.object().shape({
      sourceEntityId: yup
        .string()
        .trim()
        .required(
          t('web.components.externalReferenceItem.sourceEntityId.required')
        ),
      sourceType: yup
        .string()
        .trim()
        .required(
          t('web.components.externalReferenceItem.sourceType.required')
        ),
    }),
  });

  return yup.array().of(referenceItemSchema);
};
